import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';

import {
  InfoReviewFieldLabel,
  InfoReviewFieldValue,
} from '@/components/Form/Form.styled';
import { Divider } from '@/components/Layout/Divider';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectDefaultCurrency } from '@/redux/configs/baseSelectors';
import { selectFinancialInfo, selectUser } from '@/redux/user-data/selectors';

import { FetchedFinancialInfo, FinancialType } from '@/types/finance';
import { IncomeSource, PayFrequency } from '@/types/user';

import { ReviewCard } from './ReviewCard';
import { SectionProps } from './types';

const fieldPrefix = 'moreInfo.reviewMyInformation.finances';

export function FinancesSection({
  currentIndex,
  totalSections,
}: Readonly<SectionProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectUser);
  const financialInfo = useAppSelector(selectFinancialInfo);
  const { currency } = useAppSelector(selectDefaultCurrency);

  const assets = financialInfo.filter(
    item => item.financialType === FinancialType.Asset,
  );
  const totalAssets = assets.reduce((sum, item) => sum + item.amount, 0);
  const assetCurrency = assets[0]?.currency || currency;

  const liabilities = financialInfo.filter(
    item => item.financialType === FinancialType.Liability,
  );
  const totalLiabilities = user?.hasLiability
    ? liabilities.reduce((sum, item) => sum + item.amount, 0)
    : 0;
  const liabilityCurrency = liabilities[0]?.currency || currency;

  const { incomeSources, hasOtherIncomeSource } = useMemo(() => {
    const sourceOfIncome = user?.sourceOfIncome;
    if (!sourceOfIncome)
      return { incomeSources: '', hasOtherIncomeSource: false };
    try {
      const sources: string[] = JSON.parse(sourceOfIncome);
      return {
        incomeSources: sources
          .map(source =>
            t(`moreInfo.form.yourFinances.income.source.${source}`, {
              defaultValue: '',
            }),
          )
          .filter(Boolean)
          .join(', '),
        hasOtherIncomeSource: sources.includes(IncomeSource.Other),
      };
    } catch {
      return { incomeSources: '', hasOtherIncomeSource: false };
    }
  }, [user?.sourceOfIncome, t]);

  return (
    <ReviewCard
      title={t(`${fieldPrefix}.title`)}
      currentIndex={currentIndex}
      totalSections={totalSections}
    >
      <Box mt={1.5}>
        <TotalRow
          label={t(`${fieldPrefix}.totalAssets`)}
          value={formatAmount(totalAssets, assetCurrency)}
        />
        <BreakdownTable
          nameColumnHeader={t(`${fieldPrefix}.assetColumn`)}
          valueColumnHeader={t(`${fieldPrefix}.valueColumn`)}
          items={assets}
        />
        <Divider width="100%" sx={{ pb: 1, pt: 0.5 }} />

        <Box mt={2}>
          <TotalRow
            label={t(`${fieldPrefix}.totalLiabilities`)}
            value={formatAmount(totalLiabilities, liabilityCurrency)}
          />
          <InfoReviewFieldLabel>
            {t('moreInfo.form.yourFinances.liabilities.title')}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {user?.hasLiability ? t('common.yes') : t('common.no')}
          </InfoReviewFieldValue>
          {user?.hasLiability && (
            <BreakdownTable
              nameColumnHeader={t(`${fieldPrefix}.liabilityColumn`)}
              valueColumnHeader={t(`${fieldPrefix}.valueColumn`)}
              items={liabilities}
            />
          )}
        </Box>
        <Divider width="100%" sx={{ pb: 1, pt: 0.5 }} />

        <Box mt={1.5}>
          <TotalRow
            label={t(`${fieldPrefix}.totalMonthlyIncome`)}
            value={formatAmount(user?.totalMonthlyIncome ?? 0, currency)}
          />

          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.incomeSourcesQuestion`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>{incomeSources}</InfoReviewFieldValue>
          {hasOtherIncomeSource && (
            <>
              <InfoReviewFieldLabel>
                {t('moreInfo.form.yourFinances.income.otherIncomeSourceLabel')}
              </InfoReviewFieldLabel>
              <InfoReviewFieldValue>
                {user?.sourceOfIncomeOther}
              </InfoReviewFieldValue>
            </>
          )}
          <InfoReviewFieldLabel>
            {t(`${fieldPrefix}.payFrequencyQuestion`)}
          </InfoReviewFieldLabel>
          <InfoReviewFieldValue>
            {t(
              `moreInfo.form.yourFinances.income.frequency.${user?.howOftenPaid}`,
              {
                defaultValue: '',
              },
            )}
          </InfoReviewFieldValue>
          {user?.howOftenPaid === PayFrequency.Other && (
            <>
              <InfoReviewFieldLabel>
                {t('moreInfo.form.yourFinances.income.otherPayFrequencyLabel')}
              </InfoReviewFieldLabel>
              <InfoReviewFieldValue>
                {user?.howOftenPaidOther}
              </InfoReviewFieldValue>
            </>
          )}
        </Box>
        <Divider width="100%" sx={{ pb: 1, pt: 0.5 }} />

        <Box mt={1.5}>
          <TotalRow
            label={t(`${fieldPrefix}.totalMonthlyExpenses`)}
            value={formatAmount(user?.totalMonthlySpending ?? 0, currency)}
          />
        </Box>
      </Box>
    </ReviewCard>
  );
}

type TotalRowProps = {
  label: string;
  value: string;
};

function TotalRow({ label, value }: Readonly<TotalRowProps>) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="headlineH6">{label}</Typography>
      <Typography variant="bodyB2Medium">{value}</Typography>
    </Box>
  );
}

type BreakdownTableProps = {
  nameColumnHeader: string;
  valueColumnHeader: string;
  items: FetchedFinancialInfo[];
};

function BreakdownTable({
  nameColumnHeader,
  valueColumnHeader,
  items,
}: Readonly<BreakdownTableProps>) {
  if (items.length === 0) return null;

  return (
    <Box mt={1}>
      <Box display="flex" justifyContent="space-between" pt={0.5}>
        <Typography variant="captionC1Regular" color="neutral.700">
          {nameColumnHeader}
        </Typography>
        <Typography variant="captionC1Regular" color="neutral.700">
          {valueColumnHeader}
        </Typography>
      </Box>
      {items.map(item => (
        <Box key={item.id}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pt={2}
          >
            <Typography variant="bodyB2Regular">
              {item.financialSubtype}
            </Typography>
            <Typography variant="bodyB2Medium">
              {formatAmount(item.amount, item.currency)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function formatAmount(amount: number, currencyCode: string): string {
  return `${currencyCode} ${amount.toLocaleString('en-US')}`;
}
